import {landsTableSortableColumnData} from '#/components/lands-table/column-data';
import {LandTableRow} from '#/components/lands-table/lands-table-row';
import {filterByColumn, FilterState, useColumnSorting} from '#/components/lands-table/use-column-sorting';
import {useCoinDisplayPreferences} from '#/hooks/use-coin-display-preferences';
import {useIsElementVisible} from '#/hooks/use-is-element-visible';
import {useScrollEdges} from '#/hooks/use-is-scrolling';
import {useSelectedLand} from '#/hooks/use-selected-land';
import {ClassName, DefaultChildren} from '#/utils/utils';
import {ArrowUpIcon} from '@heroicons/react/24/solid';
import {Land, LandMarketInfo} from '@shareland/shareland-common';
import {forwardRef, FunctionComponent} from 'react';
import {useTranslation} from 'react-i18next';

type LandTableColumns = keyof typeof landsTableSortableColumnData;
type LandTableFilterState = FilterState<LandTableColumns>;

export const LandsTable: FunctionComponent<{
  landAndInfos: {land: Land; landMarketInfo: LandMarketInfo | undefined}[];
  onLandClick: (land: Land, landMarketInfo?: LandMarketInfo) => void;
}> = ({landAndInfos, onLandClick}) => {
  const {t} = useTranslation();
  const displayPref = useCoinDisplayPreferences();
  const {data: selectedLand} = useSelectedLand();

  const {columnSorting, onColumnFilter} = useColumnSorting<LandTableColumns>(undefined);

  const {scrollEdges, onScroll} = useScrollEdges();
  const isHorizontalScrolling = scrollEdges !== 'left';

  const {containerRef, elementRef, isElementVisible} = useIsElementVisible<HTMLDivElement, HTMLTableCellElement>();

  const sortedLandAndInfos = !!columnSorting
    ? filterByColumn<{land: Land; landMarketInfo: LandMarketInfo | undefined}, LandTableColumns>(
        landAndInfos,
        landsTableSortableColumnData,
        columnSorting,
      )
    : landAndInfos;

  return (
    <div className='border border-gray-300 rounded-xl overflow-hidden-clip-path @container outline-none relative'>
      <div ref={containerRef} onScroll={onScroll} className='overflow-y-hidden overflow-x-scroll'>
        <table className='w-full border-collapse text-sm @lg:text-base'>
          <colgroup>
            <col className='w-[160px] @lg:w-[300px] min-w-[160px] @lg:min-w-[300px]'></col>
            <col className='w-[120px] min-w-[auto]'></col>
            <col className='w-[140px] min-w-[auto]'></col>
            <col className='w-[120px] min-w-[auto]'></col>
            <col className='w-[120px] min-w-[auto]'></col>
            <col className='w-[120px] min-w-[auto]'></col>
            <col className='w-[120px] min-w-[auto]'></col>
            <col className='w-40 min-w-[auto]'></col>
          </colgroup>
          <thead>
            <tr className='border-b border-gray-300 font-semibold'>
              <SortableTableHeader
                column='land'
                columnSorting={columnSorting}
                onColumnFilter={onColumnFilter}
                className={`left-0 pl-4 py-6 bg-white z-10 ${isHorizontalScrolling ? 'before:right-box-shadow' : ''}`}
                spanTitleClassName='ml-10'
              >
                {t('components.locationTable.columns.location.title')}
              </SortableTableHeader>
              <SortableTableHeader
                className='px-6'
                column='price'
                columnSorting={columnSorting}
                onColumnFilter={onColumnFilter}
              >
                {t('components.locationTable.columns.price.title')}
              </SortableTableHeader>
              <SortableTableHeader
                className='px-6'
                column='change'
                columnSorting={columnSorting}
                onColumnFilter={onColumnFilter}
              >
                {t('components.locationTable.columns.change24.title')}
              </SortableTableHeader>
              <SortableTableHeader
                className='px-6'
                column='premium'
                columnSorting={columnSorting}
                onColumnFilter={onColumnFilter}
              >
                {t('components.locationTable.columns.premium.title')}
              </SortableTableHeader>
              <SortableTableHeader
                className='px-6'
                column='tvl'
                columnSorting={columnSorting}
                onColumnFilter={onColumnFilter}
              >
                {t('components.locationTable.columns.tvl.title')}
              </SortableTableHeader>
              <SortableTableHeader
                className='px-6'
                column='volume'
                columnSorting={columnSorting}
                onColumnFilter={onColumnFilter}
              >
                {t('components.locationTable.columns.volume.title')}
              </SortableTableHeader>
              <SortableTableHeader
                className='px-6'
                column='marketCap'
                columnSorting={columnSorting}
                onColumnFilter={onColumnFilter}
              >
                {t('components.locationTable.columns.marketCap.title')}
              </SortableTableHeader>
              <TableHeader ref={elementRef} className='px-6'>
                {t('components.locationTable.columns.marketPriceGraph.title')}
              </TableHeader>
            </tr>
          </thead>
          <tbody className='divide-y divide-gray-300'>
            {sortedLandAndInfos.map((landAndInfo, index) => (
              <LandTableRow
                key={`explore-discover-land-list-item-${index}`}
                selected={!!selectedLand && selectedLand.id === landAndInfo.land.id}
                landAndInfo={landAndInfo}
                onLand={onLandClick}
                displayPref={displayPref}
                isTableSticky={isHorizontalScrolling}
              />
            ))}
          </tbody>
        </table>
      </div>
      <div
        className='opacity-0 data-[visible=true]:opacity-100 absolute top-0 right-0 h-full flex pointer-events-none'
        data-visible={!isElementVisible}
      >
        <div className='h-full w-8 bg-gradient-to-l from-gray-300' />
      </div>
    </div>
  );
};

type TableHeaderProps = {
  spanTitleClassName?: string;
} & DefaultChildren &
  ClassName;
const TableHeader = forwardRef<HTMLTableCellElement, TableHeaderProps>(({className = '', children}, ref) => (
  <th ref={ref} className={`sticky top-0 text-left select-none ${className}`}>
    {children}
  </th>
));

type SortableTableHeaderProps = {
  column: LandTableFilterState['column'];
  columnSorting?: LandTableFilterState;
  onColumnFilter: (column: LandTableFilterState['column']) => void;
  spanTitleClassName?: string;
} & TableHeaderProps &
  ClassName;
const SortableTableHeader: FunctionComponent<SortableTableHeaderProps> = ({
  className,
  column,
  columnSorting,
  onColumnFilter,
  spanTitleClassName,
  children,
}) => {
  const onClick = () => {
    onColumnFilter(column);
  };

  return (
    <TableHeader className={className}>
      <button onClick={onClick} className='w-full flex items-center hover:opacity-70'>
        <div className={`${spanTitleClassName} truncate`}>{children}</div>
        <ArrowUpIcon
          className={
            'm-1 w-3.5 h-3.5 stroke-black fill-black data-[activecolumn=false]:opacity-0 pointer-events-none data-[columndirection="down"]:rotate-180'
          }
          data-activecolumn={columnSorting?.column === column}
          data-columndirection={columnSorting?.direction}
        />
      </button>
    </TableHeader>
  );
};
